import styled from "styled-components";
import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import useLectureWriting from "../../../hooks/leacture-detail/useLectureWriting";
import Modal from "../../../components/common/Modal";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import EditNoteOutlined from "@mui/icons-material/EditNoteOutlined";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";

const AI_TABS = [
  { key: "reading", label: "독해력", hint: "문제 이해·요지" },
  { key: "thinking", label: "사고력", hint: "논리·근거 구조" },
  { key: "expressiveness", label: "표현력", hint: "문장·어휘" },
  { key: "underscore", label: "밑줄", hint: "핵심 표시" },
  { key: "comprehensive", label: "통합", hint: "전체 종합" },
];

function LectureWriting({ nav, onNavChangeHandler }) {
  const [activeTab, setActiveTab] = useState("reading");
  const writingRef = useRef(null);
  const latestResultRef = useRef(null);
  const prevHistoryLen = useRef(0);

  const {
    isOpenModal,
    modalProps,
    problem,
    standard,
    writingContent,
    isLoading,
    loadingMessage,
    correctionHistory,
    handleWritingChange,
    performAllCorrections,
    submitWritingSolveData,
  } = useLectureWriting(nav, onNavChangeHandler);

  const activeMeta = useMemo(
    () => AI_TABS.find((t) => t.key === activeTab) || AI_TABS[0],
    [activeTab]
  );

  const standardText = useMemo(() => {
    if (activeTab === "comprehensive") {
      return [standard.reading, standard.thinking, standard.expressiveness, standard.underscore]
        .filter(Boolean)
        .join("\n\n");
    }
    return standard[activeTab] || "";
  }, [activeTab, standard]);

  const history = useMemo(() => {
    const map = {
      thinking: correctionHistory.thinking,
      reading: correctionHistory.reading,
      expressiveness: correctionHistory.expressiveness,
      underscore: correctionHistory.underscore,
      comprehensive: correctionHistory.comprehensive,
    };
    return map[activeTab] || [];
  }, [activeTab, correctionHistory]);

  const latest = history[0];
  const older = history.slice(1);

  useEffect(() => {
    if (history.length > prevHistoryLen.current && history.length > 0) {
      latestResultRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    prevHistoryLen.current = history.length;
  }, [history.length, activeTab]);

  const focusWriting = useCallback(() => {
    writingRef.current?.focus();
    writingRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const charCount = writingContent.length;
  const hasText = charCount > 0;

  return (
    <PageRoot>
      <Modal isOpen={isOpenModal} {...modalProps} children={modalProps.message} />

      <IntroBanner>
        <IntroIcon aria-hidden>
          <AutoAwesomeOutlined style={{ fontSize: 22 }} />
        </IntroIcon>
        <IntroCopy>
          <IntroTitle>AI 자동 첨삭</IntroTitle>
          <IntroText>
            답안을 작성한 뒤 <strong>한 번에 첨삭 받기</strong>를 누르면 독해·사고·표현·밑줄·통합 결과를 함께 받습니다.
            탭은 그 결과를 나눠 보는 용도입니다.
          </IntroText>
        </IntroCopy>
      </IntroBanner>

      <SplitLayout>
        <LeftStack>
          <InfoCard>
            <CardHeader $compact>
              <CardTitleRow>
                <EditNoteOutlined style={{ fontSize: 22, opacity: 0.95 }} aria-hidden />
                <CardTitle>논술 문제</CardTitle>
              </CardTitleRow>
            </CardHeader>
            <CardContent $tight>
              {problem && problem.question ? (
                <ProblemDetails>
                  <summary>문제 지문 보기</summary>
                  <ProblemBody>
                    <Question>{problem.question}</Question>
                    <Content>{problem.content}</Content>
                  </ProblemBody>
                </ProblemDetails>
              ) : (
                <EmptyMessage>문제가 없습니다.</EmptyMessage>
              )}
            </CardContent>
          </InfoCard>

          <InfoCard>
            <CardHeader $compact>
              <CardTitleRow>
                <CardTitle>답안 작성</CardTitle>
                <GhostButton type="button" onClick={focusWriting}>
                  작성란 포커스
                </GhostButton>
              </CardTitleRow>
              <MetaRow>
                <MetaBadge $warn={!hasText}>{hasText ? `${charCount}자` : "내용을 입력해 주세요"}</MetaBadge>
                <MetaHint>첨삭은 저장된 최신 답안 기준으로 실행됩니다.</MetaHint>
              </MetaRow>
            </CardHeader>
            <CardContent $flush>
              <WritingArea
                ref={writingRef}
                value={writingContent}
                onChange={handleWritingChange}
                placeholder="문제를 읽고 여기에 답안을 작성하세요. 작성 후 한 번에 첨삭을 받을 수 있습니다."
                spellCheck={false}
              />
            </CardContent>
          </InfoCard>
        </LeftStack>

        <RightStack>
          <AiPanel>
            <AiPanelHeader>
              <AiPanelTitle>첨삭 결과</AiPanelTitle>
              <AiPanelSub>한 번에 받은 뒤, 탭을 바꿔 유형별 결과를 봅니다.</AiPanelSub>
            </AiPanelHeader>

            <TabScroll role="tablist" aria-label="AI 첨삭 유형">
              {AI_TABS.map((tab) => (
                <TabChip
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  $active={activeTab === tab.key}
                  onClick={() => setActiveTab(tab.key)}
                >
                  <TabChipLabel>
                    {tab.label}
                    {(correctionHistory[tab.key] || []).length > 0 ? " · 결과" : ""}
                  </TabChipLabel>
                  <TabChipHint>{tab.hint}</TabChipHint>
                </TabChip>
              ))}
            </TabScroll>

            <CriteriaBlock>
              <CriteriaSummary>
                <KeyboardArrowDown style={{ fontSize: 18, flexShrink: 0 }} aria-hidden />
                <span>이 유형의 첨삭 기준 보기</span>
              </CriteriaSummary>
              <CriteriaBody>
                <CriteriaText>{standardText.trim() || "등록된 첨삭 기준이 없습니다."}</CriteriaText>
              </CriteriaBody>
            </CriteriaBlock>

            <RunBlock>
              {isLoading && (
                <InlineLoading>
                  <MiniSpinner aria-hidden />
                  <InlineLoadingText>{loadingMessage || "첨삭 준비 중…"}</InlineLoadingText>
                </InlineLoading>
              )}
              <PrimaryRunButton
                type="button"
                onClick={performAllCorrections}
                disabled={isLoading || !writingContent.trim()}
              >
                {isLoading ? "다섯 가지를 첨삭하는 중…" : "한 번에 첨삭 받기"}
              </PrimaryRunButton>
              {!writingContent.trim() && (
                <RunHint role="status">답안을 입력하면 버튼이 활성화됩니다.</RunHint>
              )}
            </RunBlock>

            <ResultsSection>
              {latest && (
                <LatestCard ref={latestResultRef}>
                  <LatestLabel>{activeMeta.label} 첨삭</LatestLabel>
                  <LatestTime>{new Date(latest.timestamp).toLocaleString("ko-KR")}</LatestTime>
                  <LatestBody>{latest.content || latest.correction}</LatestBody>
                </LatestCard>
              )}

              {older.length > 0 && (
                <OlderDetails>
                  <OlderSummary>
                    이전 첨삭 {older.length}건
                    <KeyboardArrowDown style={{ fontSize: 18, opacity: 0.6 }} aria-hidden />
                  </OlderSummary>
                  <OlderBody>
                    {older.map((item, index) => (
                      <OlderItem key={item.id || `${activeTab}-${index}`}>
                        <OlderItemHead>
                          <OlderTime>{new Date(item.timestamp).toLocaleString("ko-KR")}</OlderTime>
                          <OlderBadge>#{older.length - index}</OlderBadge>
                        </OlderItemHead>
                        <OlderContent>{item.content || item.correction}</OlderContent>
                      </OlderItem>
                    ))}
                  </OlderBody>
                </OlderDetails>
              )}

              {!latest && !isLoading && (
                <EmptyResults>이 탭에는 아직 결과가 없습니다. 한 번에 첨삭 받기를 누르면 여기에 표시됩니다.</EmptyResults>
              )}
            </ResultsSection>
          </AiPanel>
        </RightStack>
      </SplitLayout>

      <SubmitBar>
        <SubmitBarText>
          작성·첨삭이 끝나면 <strong>최종 제출</strong>을 눌러 저장합니다. 제출 후에도 답안을 수정하고 다시 첨삭할 수
          있습니다.
        </SubmitBarText>
        <SubmitButton type="button" onClick={submitWritingSolveData}>
          최종 제출
        </SubmitButton>
      </SubmitBar>
    </PageRoot>
  );
}

const PageRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding-bottom: 24px;
`;

const IntroBanner = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 18px 20px;
  border-radius: 4px;
  background: #fffbea;
  border: 1px solid #efe4bd;
`;

const IntroIcon = styled.div`
  color: #2c3561;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const IntroCopy = styled.div`
  min-width: 0;
`;

const IntroTitle = styled.h2`
  margin: 0 0 6px 0;
  font-size: 1.125rem;
  font-weight: 800;
  color: #1d2340;
  letter-spacing: -0.02em;
`;

const IntroText = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.55;
  color: #1d2340;

  strong {
    font-weight: 700;
    color: #1d2340;
  }
`;

const SplitLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 400px);
  gap: 20px;
  align-items: start;

  /* 본문 영역이 좁을 때(강의 목차와 가로 배치된 태블릿 가로 등): 2열 → 1열 세로 스택 */
  @container lectureDetailContent (max-width: 1100px) {
    grid-template-columns: 1fr;
  }

  /* 태블릿 가로·뷰포트 기준 보강 (컨테이너 쿼리 미지원·예외 케이스) */
  @media (orientation: landscape) and (max-height: 900px) and (max-width: 1366px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const LeftStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

const RightStack = styled.div`
  position: sticky;
  top: 88px;
  min-width: 0;

  @container lectureDetailContent (max-width: 1100px) {
    position: relative;
    top: 0;
  }

  @media (orientation: landscape) and (max-height: 900px) and (max-width: 1366px) {
    position: relative;
    top: 0;
  }

  @media (max-width: 960px) {
    position: relative;
    top: 0;
  }
`;

const InfoCard = styled.div`
  background: white;
  border-radius: 4px;
  box-shadow: none;
  overflow: hidden;
  border: 1px solid #e7e1d3;
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  padding: ${(p) => (p.$compact ? "16px 20px" : "20px 24px")};
  background: #1d2340;
  color: white;
`;

const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
`;

const GhostButton = styled.button`
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #e0e7ff;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 14px;
`;

const MetaBadge = styled.span`
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${(p) => (p.$warn ? "rgba(254, 226, 226, 0.95)" : "rgba(224, 231, 255, 0.95)")};
  color: ${(p) => (p.$warn ? "#991b1b" : "#1d2340")};
`;

const MetaHint = styled.span`
  font-size: 12px;
  opacity: 0.92;
  line-height: 1.4;
`;

const CardContent = styled.div`
  padding: ${(p) => {
    if (p.$flush) return "0";
    if (p.$tight) return "16px 20px";
    return "24px";
  }};
`;

const ProblemDetails = styled.details`
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;

  summary {
    list-style: none;
    cursor: pointer;
    padding: 12px 14px;
    font-size: 13px;
    font-weight: 700;
    color: #475569;
    user-select: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  summary::-webkit-details-marker {
    display: none;
  }

  &[open] summary {
    border-bottom: 1px solid #e2e8f0;
  }
`;

const ProblemBody = styled.div`
  padding: 14px 16px 16px;
`;

const Question = styled.div`
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 8px;
  color: #0f172a;
`;

const Content = styled.div`
  color: #475569;
  white-space: pre-wrap;
  line-height: 1.65;
  font-size: 14px;
`;

const EmptyMessage = styled.div`
  color: #94a3b8;
  text-align: center;
  padding: 24px;
  font-size: 14px;
`;

const WritingArea = styled.textarea`
  display: block;
  width: 100%;
  min-height: 320px;
  max-height: min(55vh, 520px);
  padding: 20px;
  border: none;
  border-radius: 0 0 20px 20px;
  font-size: 15px;
  line-height: 1.75;
  resize: vertical;
  font-family: inherit;
  background: #fafafa;
  color: #0f172a;
  box-sizing: border-box;

  &:focus {
    outline: none;
    background: #fff;
    box-shadow: inset 0 0 0 2px rgba(29, 35, 64, 0.18);
  }

  &::placeholder {
    color: #94a3b8;
  }

  @media (max-width: 960px) {
    min-height: 240px;
    max-height: none;
  }
`;

const AiPanel = styled.div`
  background: #fff;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  padding: 18px 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AiPanelHeader = styled.div``;

const AiPanelTitle = styled.h3`
  margin: 0 0 4px 0;
  font-size: 1rem;
  font-weight: 800;
  color: #0f172a;
`;

const AiPanelSub = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: #64748b;
`;

const TabScroll = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  margin: 0 -4px;
  padding-left: 4px;
  padding-right: 4px;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x proximity;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
`;

const TabChip = styled.button`
  flex: 0 0 auto;
  scroll-snap-align: start;
  min-width: 88px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$active ? "transparent" : "#e2e8f0")};
  background: ${(p) =>
    p.$active ? "#1d2340" : "#f8fafc"};
  color: ${(p) => (p.$active ? "#fff" : "#334155")};
  cursor: pointer;
  text-align: left;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${(p) => (p.$active ? "0 6px 16px rgba(79, 70, 229, 0.35)" : "0 2px 8px rgba(0,0,0,0.06)")};
  }
`;

const TabChipLabel = styled.div`
  font-size: 13px;
  font-weight: 800;
  letter-spacing: -0.02em;
`;

const TabChipHint = styled.div`
  margin-top: 2px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.25;
  opacity: 0.88;
`;

const CriteriaSummary = styled.summary`
  list-style: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  cursor: pointer;
  user-select: none;

  &::-webkit-details-marker {
    display: none;
  }

  svg {
    transition: transform 0.2s ease;
  }
`;

const CriteriaBody = styled.div`
  padding: 0 12px 12px;
`;

const CriteriaText = styled.div`
  max-height: 200px;
  overflow-y: auto;
  font-size: 13px;
  line-height: 1.6;
  color: #475569;
  white-space: pre-wrap;
`;

const CriteriaBlock = styled.details`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;

  &[open] summary svg {
    transform: rotate(180deg);
  }
`;

const RunBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const InlineLoading = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fffbea;
  border: 1px solid #e7e1d3;
`;

const MiniSpinner = styled.div`
  width: 22px;
  height: 22px;
  border: 2px solid #e7e1d3;
  border-top-color: #2c3561;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const InlineLoadingText = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #3730a3;
  line-height: 1.4;
`;

const PrimaryRunButton = styled.button`
  width: 100%;
  padding: 14px 16px;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 800;
  color: #fff;
  cursor: pointer;
  background: #1d2340;
  box-shadow: 0 4px 14px rgba(79, 70, 229, 0.35);
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 22px rgba(79, 70, 229, 0.4);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const RunHint = styled.p`
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
`;

const ResultsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LatestCard = styled.div`
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid #e7e1d3;
  background: linear-gradient(180deg, #fafaff 0%, #fff 100%);
`;

const LatestLabel = styled.div`
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #2c3561;
  margin-bottom: 4px;
`;

const LatestTime = styled.div`
  font-size: 11px;
  color: #64748b;
  margin-bottom: 10px;
`;

const LatestBody = styled.div`
  font-size: 14px;
  line-height: 1.65;
  color: #1e293b;
  white-space: pre-wrap;
`;

const OlderDetails = styled.details`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
`;

const OlderSummary = styled.summary`
  list-style: none;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;

  &::-webkit-details-marker {
    display: none;
  }
`;

const OlderBody = styled.div`
  padding: 0 4px 8px;
`;

const OlderItem = styled.div`
  padding: 12px;
  margin: 0 8px 8px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
`;

const OlderItemHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const OlderTime = styled.span`
  font-size: 11px;
  color: #64748b;
`;

const OlderBadge = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #2563eb;
  background: #dbeafe;
  padding: 2px 8px;
  border-radius: 999px;
`;

const OlderContent = styled.div`
  font-size: 13px;
  line-height: 1.55;
  color: #334155;
  white-space: pre-wrap;
`;

const EmptyResults = styled.p`
  margin: 0;
  padding: 16px;
  text-align: center;
  font-size: 13px;
  color: #94a3b8;
  line-height: 1.5;
  border-radius: 12px;
  border: 1px dashed #e2e8f0;
  background: #fafafa;
`;

const SubmitBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 18px 20px;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
`;

const SubmitBarText = styled.p`
  margin: 0;
  flex: 1;
  min-width: 200px;
  font-size: 13px;
  line-height: 1.5;
  color: #475569;

  strong {
    color: #0f172a;
  }
`;

const SubmitButton = styled.button`
  padding: 12px 28px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  border: none;
  color: #fff;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  box-shadow: 0 2px 10px rgba(5, 150, 105, 0.35);
  flex-shrink: 0;

  &:hover {
    filter: brightness(1.05);
  }
`;

export default LectureWriting;
